// hooks/tickets/useTickets.ts
import { useState, useEffect, useCallback } from "react";
import { Ticket, BaseTicket, TicketStats, TicketFilters } from "../../../types/features/support/Ticket";
import { ticketService } from "../../../services/support/ticketService";
import { PaginatedResponse } from "../../../types/utils/PaginatedResponse";
import { sortByDate } from "../../../helpers/sortDataByDate";

export default function useTickets() {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<TicketStats | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    lastPage: 1,
    total: 0,
    perPage: 20,
  });

  const fetchTickets = useCallback(async (page: number = 1, perPage: number = 20, filters?: TicketFilters) => {
    setLoading(true);
    setError(null);
    try {
      const response: PaginatedResponse<Ticket> = await ticketService.getTickets(page, perPage, filters);

      const dataEnrichies = response.data.map((item) => {
        return {
          ...item,
          client_name: `${item.client?.firstname} ${item.client?.lastname}`,
          client_phone: item.client?.phone,
        };
      });

      const sortedTickets = sortByDate(dataEnrichies, (ticket) => ticket.created_at);
      setTickets(sortedTickets);

      setPagination({
        currentPage: response.current_page,
        lastPage: response.last_page,
        total: response.total,
        perPage: response.per_page,
      });
    } catch (err) {
      setError("Erreur lors du chargement des tickets");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTicketById = useCallback(async (id: string): Promise<Ticket | null> => {
    setLoading(true);
    setError(null);
    try {
      const ticket = await ticketService.getTicketById(id);
      if (!ticket) setError("Ticket non trouvé");
      return ticket;
    } catch (err) {
      setError("Erreur lors du chargement du ticket");
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTicketsByClient = useCallback(
    async (clientId: string, page: number = 1, perPage: number = 20): Promise<PaginatedResponse<Ticket>> => {
      setLoading(true);
      setError(null);
      try {
        const response = await ticketService.getTicketsByClient(clientId, page, perPage);
        return response;
      } catch (err) {
        setError("Erreur lors du chargement des tickets du client");
        console.error(err);
        return {
          current_page: 1,
          data: [],
          first_page_url: "",
          from: null,
          last_page: 1,
          last_page_url: "",
          links: [],
          next_page_url: null,
          path: "",
          per_page: perPage,
          prev_page_url: null,
          to: null,
          total: 0,
        };
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const createTicket = useCallback(
    async (data: Omit<Ticket, "id" | "created_at" | "updated_at">): Promise<Ticket | null> => {
      setLoading(true);
      setError(null);
      try {
        const newTicket = await ticketService.createTicket(data);
        setTickets((prev) => [newTicket, ...prev]);
        return newTicket;
      } catch (err) {
        setError("Erreur lors de la création du ticket");
        console.error(err);
        return null;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const updateTicket = useCallback(async (id: string, data: Partial<BaseTicket>): Promise<Ticket | null> => {
    setLoading(true);
    setError(null);
    try {
      const updatedTicket = await ticketService.updateTicket(id, data);
      setTickets((prev) => prev.map((t) => (t.id === id ? updatedTicket : t)));
      return updatedTicket;
    } catch (err) {
      setError("Erreur lors de la mise à jour du ticket");
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const assignAgent = useCallback(async (ticketId: string, agentId: string): Promise<Ticket | null> => {
    setLoading(true);
    setError(null);
    try {
      const updatedTicket = await ticketService.assignAgent(ticketId, agentId);
      setTickets((prev) => prev.map((t) => (t.id === ticketId ? updatedTicket : t)));
      return updatedTicket;
    } catch (err) {
      setError("Erreur lors de l'assignation de l'agent");
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const escalateTicket = useCallback(async (ticketId: string): Promise<Ticket | null> => {
    setLoading(true);
    setError(null);
    try {
      const updatedTicket = await ticketService.escalateTicket(ticketId);
      setTickets((prev) => prev.map((t) => (t.id === ticketId ? updatedTicket : t)));
      return updatedTicket;
    } catch (err) {
      setError("Erreur lors de l'escalade du ticket");
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const resolveTicket = useCallback(async (ticketId: string): Promise<Ticket | null> => {
    setLoading(true);
    setError(null);
    try {
      const updatedTicket = await ticketService.resolveTicket(ticketId);
      setTickets((prev) => prev.map((t) => (t.id === ticketId ? updatedTicket : t)));
      return updatedTicket;
    } catch (err) {
      setError("Erreur lors de la résolution du ticket");
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteTicket = useCallback(async (id: string): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      await ticketService.deleteTicket(id);
      setTickets((prev) => prev.filter((t) => t.id !== id));
      return true;
    } catch (err) {
      setError("Erreur lors de la suppression du ticket");
      console.error(err);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await ticketService.getStats();
      setStats(data);
    } catch (err) {
      setError("Erreur lors du chargement des statistiques");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTickets();
    fetchStats();
  }, [fetchTickets, fetchStats]);

  return {
    tickets,
    loading,
    error,
    stats,
    pagination,
    fetchTickets,
    fetchTicketById,
    fetchTicketsByClient,
    createTicket,
    updateTicket,
    assignAgent,
    escalateTicket,
    resolveTicket,
    deleteTicket,
    fetchStats,
  };
}
